import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogIn, Phone, KeyRound, Loader2, ArrowRight, Megaphone, UserPlus, Download, Share, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { SCHOOL_LOGO, SCHOOL_NAME } from "../../lib/constants";
import { guardianLogin, guardianRegister, setGuardianToken } from "../lib/pickupApi";

export default function PickupLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    setIsStandalone(standalone);
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);

  const handleInstallClick = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === "accepted") setInstallPrompt(null);
    } else if (isIos) {
      setShowIosHelp(true);
    } else {
      toast.error("التطبيق مثبّت بالفعل، أو متصفحك لا يدعم التثبيت المباشر");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !password || (mode === "register" && !fullName)) {
      toast.error("الرجاء تعبئة جميع الحقول");
      return;
    }
    setLoading(true);
    try {
      const res = mode === "login"
        ? await guardianLogin({ phone, password })
        : await guardianRegister({ phone, password, full_name: fullName });
      setGuardianToken(res.token);
      toast.success(mode === "login" ? "مرحباً بك" : "تم إنشاء حسابك بنجاح");
      nav("/pickup/children", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "تعذر تسجيل الدخول");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen flex flex-col md:flex-row" data-testid="pickup-login-page">
      {/* اليسار: خلفية ترحيبية */}
      <div className="hidden md:flex md:w-1/2 relative bg-gradient-to-br from-sky-700 via-sky-600 to-teal-700 items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-0 -left-24 w-96 h-96 rounded-full bg-amber-300 blur-3xl" />
        </div>
        <div className="relative z-10 text-center px-10 max-w-md">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/30 mb-6">
            <Megaphone size={38} className="text-white" strokeWidth={2} />
          </div>
          <h1 className="font-cairo text-3xl font-black text-white leading-tight">
            مرحبًا بك في نظام استدعاء الطلاب
          </h1>
          <p className="mt-4 text-white/85 text-sm leading-relaxed">
            استدعِ ابنك بسهولة وسرعة، وتابع حالة الطلب لحظة بلحظة حتى استلامه بأمان.
          </p>
          <div className="mt-8 flex items-center justify-center gap-2 text-white/70 text-xs">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center overflow-hidden">
              <img src={SCHOOL_LOGO} alt="" className="w-6 h-6 object-contain" />
            </div>
            <span className="font-cairo">{SCHOOL_NAME}</span>
          </div>
        </div>
      </div>

      {/* اليمين: نموذج الدخول */}
      <div className="flex-1 flex items-center justify-center bg-[#F6FAF9] p-4 relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none md:hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-sky-500/10 blur-3xl" />
        </div>
        <div className="relative w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-sky-700 text-xs font-cairo mb-3">
            <ArrowRight size={14} className="rotate-180" /> العودة إلى بوابة الأنظمة
          </Link>

          {!isStandalone && (installPrompt || isIos) && (
            <button
              onClick={handleInstallClick}
              className="w-full mb-4 flex items-center justify-center gap-2 h-11 rounded-xl bg-white border border-sky-200 text-sky-700 hover:bg-sky-50 font-cairo font-bold text-sm shadow-sm"
              data-testid="pickup-install-app"
            >
              <Download size={16} /> ثبّت التطبيق على جهازك
            </button>
          )}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-sky-50 ring-1 ring-sky-200/60 flex items-center justify-center">
                  <Megaphone size={22} className="text-sky-700" />
                </div>
                <div>
                  <h2 className="font-cairo font-extrabold text-slate-900 text-lg">
                    {mode === "login" ? "تسجيل الدخول" : "إنشاء حساب جديد"}
                  </h2>
                  <p className="text-xs text-slate-500">بوابة أولياء الأمور</p>
                </div>
              </div>

              <form onSubmit={onSubmit} className="space-y-4">
                {mode === "register" && (
                  <div className="space-y-1.5">
                    <Label>اسم ولي الأمر</Label>
                    <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="الاسم الكامل" className="h-11" data-testid="pickup-fullname" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>رقم الجوال</Label>
                  <div className="relative">
                    <Phone className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05xxxxxxxx" className="h-11 pr-9" dir="ltr" data-testid="pickup-phone" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>كلمة المرور</Label>
                  <div className="relative">
                    <KeyRound className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••" className="h-11 pr-9" data-testid="pickup-password" />
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full h-11 bg-sky-700 hover:bg-sky-800 text-white gap-2 font-cairo font-bold" data-testid="pickup-submit">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : mode === "login" ? <LogIn size={16} /> : <UserPlus size={16} />}
                  {mode === "login" ? "تسجيل الدخول" : "إنشاء الحساب"}
                </Button>
              </form>

              <div className="mt-5 text-center text-sm">
                {mode === "login" ? (
                  <button onClick={() => setMode("register")} className="text-sky-700 hover:underline font-cairo" data-testid="pickup-switch-register">
                    ليس لديك حساب؟ إنشاء حساب جديد
                  </button>
                ) : (
                  <button onClick={() => setMode("login")} className="text-sky-700 hover:underline font-cairo" data-testid="pickup-switch-login">
                    لديك حساب بالفعل؟ تسجيل الدخول
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showIosHelp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowIosHelp(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-right" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-cairo font-extrabold text-slate-900">تثبيت التطبيق على آيفون</h3>
              <button onClick={() => setShowIosHelp(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <ol className="space-y-3 text-sm text-slate-700 font-cairo leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-xs">1</span>
                <span>افتح هذه الصفحة من متصفح <b>Safari</b>.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-xs">2</span>
                <span className="flex items-center gap-1.5">اضغط زر المشاركة <Share size={15} className="inline text-sky-700" /> بالأسفل.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-xs">3</span>
                <span>اختر <b>"إضافة إلى الشاشة الرئيسية"</b> من القائمة.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-xs">4</span>
                <span>اضغط <b>"إضافة"</b> — ستظهر أيقونة التطبيق على شاشتك.</span>
              </li>
            </ol>
            <Button onClick={() => setShowIosHelp(false)} className="w-full h-10 mt-5 bg-sky-700 hover:bg-sky-800 text-white font-cairo">فهمت</Button>
          </div>
        </div>
      )}
    </div>
  );
}
